import { RefreshCw } from 'lucide-react';
import type { Token } from '../../api/types';
import { formatDate } from '../../lib/format';
import CopyButton from '../CopyButton';
import QrCode from '../QrCode';
import { Badge, Button, EmptyState, Section } from '../ui';
import type { BadgeVariant } from '../ui';

function tokenState(token: Token): { variant: BadgeVariant; label: string } {
  if (token.revokedAt) return { variant: 'error', label: `révoqué le ${formatDate(token.revokedAt)}` };
  if (token.consumedAt) return { variant: 'jupiter', label: `consommé le ${formatDate(token.consumedAt)}` };
  return { variant: 'success', label: 'disponible' };
}

export default function LiensSection({
  tokens,
  onRegenerate,
  busy,
}: {
  tokens: Token[];
  onRegenerate: () => void;
  busy: boolean;
}) {
  return (
    <Section
      title="Liens d’import"
      actions={
        <Button size="sm" variant="primary" onClick={onRegenerate} disabled={busy}>
          <RefreshCw size={14} aria-hidden />
          Régénérer
        </Button>
      }
    >
      <p className="mb-3 text-xs/4 text-outline">
        Un lien ne sert qu’une fois. « Régénérer » remplace le lien actuel par un nouveau (l’ancien ne fonctionne plus).
      </p>
      {tokens.length === 0 ? <EmptyState>Aucun lien pour cette commande.</EmptyState> : null}
      <ul className="space-y-3">
        {tokens.map((token) => {
          const state = tokenState(token);
          const usable = !token.consumedAt && !token.revokedAt;
          return (
            <li key={token.x} className="flex flex-wrap items-start gap-4 rounded-lg border border-card-border bg-surface-inset p-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={state.variant}>{state.label}</Badge>
                  <Badge variant="outline">{token.type === 't' ? 'transfert' : 'import'}</Badge>
                  <span className="text-xs text-outline">créé le {formatDate(token.createdAt)}</span>
                </div>
                <p className="mt-2 break-all font-mono text-xs text-on-surface-variant">{token.url}</p>
                <div className="mt-2">
                  <CopyButton value={token.url} label="Copier le lien" />
                </div>
              </div>
              {usable ? (
                <div className="rounded-lg bg-white p-2">
                  <QrCode value={token.url} />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
